// LLM Service for generating narratives
// This service handles communication with Anthropic's Claude API and OpenAI API

import { CGIAR_STYLE_GUIDE } from './styleGuide.js';

// Model options for each provider
export const MODEL_OPTIONS = {
  awsBatch: [
    { value: 'claude-opus-4-6', label: 'Claude Opus 4.6 (1M)' },
    { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6' },
    { value: 'claude-sonnet-4-20250514', label: 'Claude Sonnet 4' },
    { value: 'claude-haiku-3-5-20241022', label: 'Claude 3.5 Haiku' },
    { value: 'gpt-4.1', label: 'GPT-4.1' },
    { value: 'gpt-4o', label: 'GPT-4o' },
    { value: 'gpt-4.1-mini', label: 'GPT-4.1 Mini' },
  ],
};

// ── Model context-window limits (shared with NarrativeEditor) ────────────────
export const MODEL_CONTEXT_LIMITS = {
  'claude-opus-4-6': 1_000_000,
  'claude-sonnet-4-6': 200_000,
  'claude-sonnet-4-20250514': 200_000,
  'claude-haiku-3-5-20241022': 200_000,
  'gpt-4.1': 1_000_000,
  'gpt-4o': 128_000,
  'gpt-4.1-mini': 1_000_000,
};

// Use 80% of the context window for input — leaves room for output tokens + overhead
const CHUNKING_SAFETY_FACTOR = 0.80;

// Maximum characters per chunk prompt — keeps Excel cell-splitting manageable.
// The batch service transports prompts via Excel cells (32K char limit each).
// Very large prompts need many rows with document_id grouping, which becomes
// unreliable beyond ~60 rows. 640K chars ≈ 20 cells, ~160K tokens.
const MAX_CHUNK_CHARS = 640_000;

// Simple token estimator (~4 chars per token)
function estimateTokens(text) {
  return Math.round((text || '').length / 4);
}

// ── Shared prompt rules (injected into every section prompt) ──────────────────

const SHARED_RULES = `Write in a professional yet accessible tone. Avoid jargon. Use active voice.
Follow the writing conventions in the <style-guide> tag (spelling, capitalization, punctuation, terminology, and formatting).
If a <description> tag is provided, it contains the program's comprehensive context document — its vision, objectives, Areas of Work, theory of change, and strategic framing. Use it to ground your writing in the program's identity, priorities, and terminology, and to connect results to the relevant objectives and program goals. Do not include its content as factual claims; use it only for framing and reference.

For EVERY factual statement derived from the data, embed evidence links using ONLY this exact markdown syntax: [descriptive phrase](Rxx) where xx is the result id from the data. The link MUST wrap a natural descriptive phrase, NOT appear as a bare citation.

CORRECT: "[Improved maize varieties reached 50,000 farmers](R17) across three countries."
CORRECT: "The program [released 40 climate-resilient wheat varieties](R23020) across Africa."
WRONG: "...released 40 wheat varieties [23020]." — NEVER use bare bracket citations.
WRONG: "...across multiple countries [23020; 24438; 24729]." — NEVER use semicolon-separated number lists.
WRONG: "...new potato varieties [28581-28622]." — NEVER use ranges.

Each link must reference exactly ONE result id (e.g., R17, R23020). When multiple results support a claim, cite ALL of them — list each (Rxx) separately after the phrase. Aim for at least one citation per sentence that references a specific result, country, number, or achievement from the data.`;

// ── Section-specific task descriptions ───────────────────────────────────────

const SECTION_TASKS = {
  executiveSummary: `Based on the results data provided, write a compelling executive summary (max 750 words) for a CGIAR {entityType} annual report.

Focus on:
- Major achievements, challenges, and shifts that shaped performance during the year
- The most significant results, highlighting how they contribute to expected outcomes
- Quality and coherence over comprehensive detail`,

  progressNarrative: `Based on the results data provided, write a concise narrative (max 500 words) about progress along the theory of change for a CGIAR program annual report.

Focus on:
- What was achieved against the Theory of Change
- Why it matters
- How it contributes to 2030 outcomes
- Contributions toward relevant Impact Areas

Include specific examples where possible.`,

  areaOfWorkProgress: `Based on the results data provided, write a progress summary (max 500 words) for the Area of Work described in context.

Include:
- Key achievements
- Progress against the planned indicators listed in context
- Portfolio linkages if relevant
- W3/Bilateral collaboration highlights`,

};

// ── Context blocks (optional, per section) ───────────────────────────────────

const SECTION_CONTEXT = {
  progressNarrative: `<context>
Program: {aow}
Impact Areas: {impactAreas}
</context>`,

  areaOfWorkProgress: `<context>
Area of Work: {aow}
Targets from Theory of Change:
{targets}
</context>`,

};

// ── Build full prompt from parts ─────────────────────────────────────────────

function buildPrompt(sectionKey) {
  const task = SECTION_TASKS[sectionKey];
  const context = SECTION_CONTEXT[sectionKey] || '';
  const dataBlock = '\n<data>{results}</data>';
  const descriptionBlock = '\n\n<description>\n{description}\n</description>';
  const styleGuideBlock = '\n\n<style-guide>\n{styleGuide}\n</style-guide>';

  return `<instructions>\n${task}\n\n${SHARED_RULES}\n</instructions>${context ? '\n' + context : ''}${styleGuideBlock}${descriptionBlock}${dataBlock}`;
}

// Default prompts for each section — assembled from shared rules + section tasks
export const DEFAULT_PROMPTS = Object.fromEntries(
  Object.keys(SECTION_TASKS).map(key => [key, buildPrompt(key)])
);

// Override executive summary with a dedicated prompt (v3) — self-contained, includes
// its own citation rules, explicit guidance on <description> tag usage, and {entityName}.
DEFAULT_PROMPTS.executiveSummary = `<instructions>
You are a senior writer for the CGIAR annual report. You are given below a dataset of results data from a CGIAR {entityType}, {entityName}; and you are to write a clear and objective executive summary of its annual progress for 2025.

<requirements>
- FOCUS on the major achievements, challenges, and shifts that shaped performance during the year. Outline the most significant results, highlighting how they contribute to the expected outcomes.
- DO NOT include values or informations that comes from the content between the <description> XML tags.
- ONLY USE information from between the <description> XML tags for framing or reference purposes of the executive summary. The <description> tag contains the program's comprehensive context document — its vision, objectives, Areas of Work, theory of change, and strategic framing. Use it to ground your writing in the program's identity, priorities, and terminology, and to connect results to the relevant objectives and program goals.
- For the actual executive summary, ONLY USE or reference the information from between the <data> XML tags.
- Emphasize quality and coherence over comprehensive detail.
- The executive summary should have a max length of 750 words.
- The executive summary should be a clear and objective narrative with a flow.
- The executive summary should have a coherent structure with a clear beginning, middle, and end; and the headings should clearly mark what they are about.
- The executive summary should be written in a professional yet accessible tone, no jargon or acronyms.
- Follow the writing conventions in the <style-guide> tag (spelling, capitalization, punctuation, terminology, and formatting).
- For EVERY factual statement derived from the data, embed evidence links using ONLY this exact markdown syntax: [descriptive phrase](Rxx) where xx is the result id from the data. The link MUST wrap a natural descriptive phrase, NOT appear as a bare citation.
- For the citations, put them after the descriptive phrase, not as a footnote. And put the hyperlinked text in parenthesis to separate it from the descriptive phrase.
- YOU CANNOT have paragraphs without citations or references.

<citation-examples>
CORRECT: "[Improved maize varieties reached 50,000 farmers](R17) across three countries."
CORRECT: "The program [released 40 climate-resilient wheat varieties](R23020) across Africa."
WRONG: "...released 40 wheat varieties [23020]." — NEVER use bare bracket citations.
WRONG: "...across multiple countries [23020; 24438; 24729]." — NEVER use semicolon-separated number lists.
WRONG: "...new potato varieties [28581-28622]." — NEVER use ranges.
</citation-examples>
</requirements>

Each link must reference exactly ONE result id (e.g., R17, R23020). When multiple results support a claim, cite ALL of them — list each (Rxx) separately after the phrase. Aim for at least one citation per sentence that references a specific result, country, number, or achievement from the data.
</instructions>

<style-guide>
{styleGuide}
</style-guide>

<description>
{description}
</description>

<data>
{results}
</data>`;

// Override AoW progress with a dedicated prompt — self-contained, includes
// its own citation rules, explicit guidance on <description> tag usage, and {entityName}.
// Uses {areaOfWorkName} placeholder (resolved from data.aow in formatPrompt).
DEFAULT_PROMPTS.areaOfWorkProgress = `<instructions>
You are a senior writer for the CGIAR annual report. You are given below a dataset of results data from a CGIAR {entityType}, {entityName}, for a specific Area of Work: {areaOfWorkName}; and you are to write a clear and objective narrative of its progress for 2025.

<requirements>
- FOCUS on the headline results and progress delivered during the year for this Area of Work, combining evidence-based narrative with key achievements.
- DO NOT include values or information that comes from the content between the <description> XML tags.
- ONLY USE information from between the <description> XML tags for framing or reference purposes of the progress narrative. The <description> tag contains the program's comprehensive context document — its vision, objectives, Areas of Work, theory of change, and strategic framing. Use it to ground your writing in the program's identity, priorities, and terminology, and to connect results to the relevant objectives and program goals.
- For the actual progress narrative, ONLY USE or reference the information from between the <data> XML tags.
- Ground the narrative in objectively verifiable evidence of progress against agreed key performance indicators and targets, rather than self-reported perceptions of progress.
- Where relevant, highlight portfolio linkages — key results achieved together with other Programs or Accelerators — and partnerships with external partners.
- Where relevant, highlight W3- and bilateral-funded work contributing to this Area of Work.
- Where the data supports it, include a short highlight (a specific result, project, or country example) that illustrates a notable achievement within this Area of Work.
- Emphasize quality and coherence over comprehensive detail.
- The progress narrative should have a max length of 500 words.
- The progress narrative should be a clear and objective narrative with a flow.
- The progress narrative should have a coherent structure with a clear beginning, middle, and end; and the headings should clearly mark what they are about.
- The progress narrative should be written in a professional yet accessible tone, no jargon or acronyms.
- Follow the writing conventions in the <style-guide> tag (spelling, capitalization, punctuation, terminology, and formatting).
- For EVERY factual statement derived from the data, embed evidence links using ONLY this exact markdown syntax: [descriptive phrase](Rxx) where xx is the result id from the data. The link MUST wrap a natural descriptive phrase, NOT appear as a bare citation.
- For the citations, put them after the descriptive phrase, not as a footnote. And put the hyperlinked text in parenthesis to separate it from the descriptive phrase.
- YOU CANNOT have paragraphs without citations or references.

<citation-examples>
CORRECT: "[Improved maize varieties reached 50,000 farmers](R17) across three countries."
CORRECT: "The program [released 40 climate-resilient wheat varieties](R23020) across Africa."
WRONG: "...released 40 wheat varieties [23020]." — NEVER use bare bracket citations.
WRONG: "...across multiple countries [23020; 24438; 24729]." — NEVER use semicolon-separated number lists.
WRONG: "...new potato varieties [28581-28622]." — NEVER use ranges.
</citation-examples>
</requirements>

Each link must reference exactly ONE result id (e.g., R17, R23020). When multiple results support a claim, cite ALL of them — list each (Rxx) separately after the phrase. Aim for at least one citation per sentence that references a specific result, country, number, or achievement from the data.
</instructions>

<style-guide>
{styleGuide}
</style-guide>

<description>
{description}
</description>

<data>
{results}
</data>`;

// Override progress narrative with a dedicated prompt (v2) — self-contained, includes
// its own citation rules, explicit guidance on <description> tag usage, and {entityName}.
DEFAULT_PROMPTS.progressNarrative = `<instructions>
You are a senior writer for the CGIAR annual report. You are given below a dataset of results data from a CGIAR {entityType}, {entityName}; and you are to write a clear and objective narrative of its progress along its theory of change for 2025.

<requirements>
- FOCUS on what was achieved against the theory of change, why it matters, and how it contributes to the {entityType}'s 2030 outcomes.
- DO NOT include values or information that comes from the content between the <description> XML tags.
- ONLY USE information from between the <description> XML tags for framing or reference purposes of the progress narrative. The <description> tag contains the program's comprehensive context document — its vision, objectives, Areas of Work, theory of change, and strategic framing. Use it to ground your writing in the program's identity, priorities, and terminology, and to connect results to the relevant objectives and program goals.
- For the actual progress narrative, ONLY USE or reference the information from between the <data> XML tags.
- Include a description of contributions toward the Impact Areas that are relevant to the {entityType}'s theory of change.
- Outline the key areas of progress, relevant examples, and any significant enabling factors or challenges that shaped results during the year.
- Where relevant, incorporate {entityType}-specific data and metrics that strengthen the evidence base.
- Where relevant, highlight country-focused summaries of results, relevant impact studies, and portfolio linkages with other Programs or Accelerators.
- Emphasize quality and coherence over comprehensive detail.
- The progress narrative should have a max length of 500 words.
- The progress narrative should be a clear and objective narrative with a flow.
- The progress narrative should have a coherent structure with a clear beginning, middle, and end; and the headings should clearly mark what they are about.
- The progress narrative should be written in a professional yet accessible tone, no jargon or acronyms.
- Follow the writing conventions in the <style-guide> tag (spelling, capitalization, punctuation, terminology, and formatting).
- For EVERY factual statement derived from the data, embed evidence links using ONLY this exact markdown syntax: [descriptive phrase](Rxx) where xx is the result id from the data. The link MUST wrap a natural descriptive phrase, NOT appear as a bare citation.
- For the citations, put them after the descriptive phrase, not as a footnote. And put the hyperlinked text in parenthesis to separate it from the descriptive phrase.
- YOU CANNOT have paragraphs without citations or references.

<citation-examples>
CORRECT: "[Improved maize varieties reached 50,000 farmers](R17) across three countries."
CORRECT: "The program [released 40 climate-resilient wheat varieties](R23020) across Africa."
WRONG: "...released 40 wheat varieties [23020]." — NEVER use bare bracket citations.
WRONG: "...across multiple countries [23020; 24438; 24729]." — NEVER use semicolon-separated number lists.
WRONG: "...new potato varieties [28581-28622]." — NEVER use ranges.
</citation-examples>
</requirements>

Each link must reference exactly ONE result id (e.g., R17, R23020). When multiple results support a claim, cite ALL of them — list each (Rxx) separately after the phrase. Aim for at least one citation per sentence that references a specific result, country, number, or achievement from the data.
</instructions>

<style-guide>
{styleGuide}
</style-guide>

<description>
{description}
</description>

<data>
{results}
</data>`;

// Override partnership/portfolio linkages with a dedicated prompt — self-contained, handles
// both Programs and Accelerators via conditional language within the same prompt.
DEFAULT_PROMPTS.partnershipOverview = `<instructions>
You are a senior writer for the CGIAR annual report. You are given below a dataset of results data from a CGIAR {entityType}, {entityName}; and you are to write a clear and objective narrative of its portfolio linkages and partnerships for 2025.

<requirements>
- IF the report is about a Program: FOCUS on the strength, strategic value, diversity, and contribution of partnerships to results, and how they contributed to overall theory of change progress and progress toward 2030 outcomes.
- IF the report is about an Accelerator: FOCUS on the strategic value, diversity, and contribution to the wider CGIAR Portfolio, and, where relevant, the strength, strategic value, diversity, and contribution of partnerships to results.
- DO NOT include values or information that comes from the content between the <description> XML tags.
- ONLY USE information from between the <description> XML tags for framing or reference purposes of the narrative. The <description> tag contains the program's comprehensive context document — its vision, objectives, Areas of Work, theory of change, and strategic framing. Use it to ground your writing in the program's identity, priorities, and terminology, and to connect results to the relevant objectives and program goals.
- For the actual narrative, ONLY USE or reference the information from between the <data> XML tags.
- Describe partnerships in a manner consistent with CGIAR's Engagement Framework for Partnerships and Advocacy, emphasizing strategic partner roles, co-ownership, and contribution to outcomes rather than merely listing partners.
- Where the data supports it, describe the role of NARES partners, focusing on co-design, implementation, capacity sharing, or scaling of results — not just listing their participation.
- Where the data supports it, highlight learning, adaptation, or co-creation enabled through partnerships.
- IF the report is about an Accelerator: describe how portfolio linkages contributed to overall theory of change progress and progress toward 2030 outcomes. Where external partnerships are central to delivering results, also cover partnerships.
- Where the data supports it, include a short illustrative example (outcome-focused or country-focused) that demonstrates a notable portfolio linkage or partnership contribution.
- Where relevant, incorporate {entityType}-specific data and metrics that complement standard results reporting and help illustrate portfolio linkage and partnership contributions.
- Emphasize quality and coherence over comprehensive detail.
- The narrative should have a max length of 1,000 words.
- The narrative should be a clear and objective narrative with a flow.
- The narrative should have a coherent structure with a clear beginning, middle, and end; and the headings should clearly mark what they are about.
- The narrative should be written in a professional yet accessible tone, no jargon or acronyms.
- Follow the writing conventions in the <style-guide> tag (spelling, capitalization, punctuation, terminology, and formatting).
- For EVERY factual statement derived from the data, embed evidence links using ONLY this exact markdown syntax: [descriptive phrase](Rxx) where xx is the result id from the data. The link MUST wrap a natural descriptive phrase, NOT appear as a bare citation.
- For the citations, put them after the descriptive phrase, not as a footnote. And put the hyperlinked text in parenthesis to separate it from the descriptive phrase.
- YOU CANNOT have paragraphs without citations or references.

<citation-examples>
CORRECT: "[Improved maize varieties reached 50,000 farmers](R17) across three countries."
CORRECT: "The program [released 40 climate-resilient wheat varieties](R23020) across Africa."
WRONG: "...released 40 wheat varieties [23020]." — NEVER use bare bracket citations.
WRONG: "...across multiple countries [23020; 24438; 24729]." — NEVER use semicolon-separated number lists.
WRONG: "...new potato varieties [28581-28622]." — NEVER use ranges.
</citation-examples>
</requirements>

Each link must reference exactly ONE result id (e.g., R17, R23020). When multiple results support a claim, cite ALL of them — list each (Rxx) separately after the phrase. Aim for at least one citation per sentence that references a specific result, country, number, or achievement from the data.
</instructions>

<style-guide>
{styleGuide}
</style-guide>

<description>
{description}
</description>

<data>
{results}
</data>`;

// Both section keys use the same prompt — conditional language handles Program vs Accelerator
DEFAULT_PROMPTS.portfolioLinkagesOverview = DEFAULT_PROMPTS.partnershipOverview;

// ── Portfolio Narrative prompts ─────────────────────────────────────────────

DEFAULT_PROMPTS.portfolioOverview = `<instructions>
You are a senior writer for the CGIAR annual Portfolio Narrative report. You are given below a dataset of results data from across the entire CGIAR Portfolio; and you are to write a clear and objective overview of Portfolio-level context and progress for 2025.

<requirements>
- Summarize Portfolio context, referring where relevant to key elements of the transition to the new 2025-2030 Portfolio, including management arrangements, the inception period, early operationalization of performance and results management arrangements, W3/Bilateral mapping, prioritization processes, and 2025 progress in strengthening Portfolio cohesion and coherence.
- Briefly note the implications for 2026.
- This is a Portfolio-level synthesis — DO NOT describe individual Programs in detail; instead, highlight Portfolio-wide patterns, trends, and achievements.
- Emphasize quality and coherence over comprehensive detail.
- The narrative should have a max length of 500 words.
- The narrative should be written in a professional yet accessible tone, no jargon or acronyms.
- Follow the writing conventions in the <style-guide> tag.
- For EVERY factual statement derived from the data, embed evidence links using ONLY this exact markdown syntax: [descriptive phrase](Rxx) where xx is the result id from the data.
- YOU CANNOT have paragraphs without citations or references.

<citation-examples>
CORRECT: "[Improved maize varieties reached 50,000 farmers](R17) across three countries."
WRONG: "...released 40 wheat varieties [23020]." — NEVER use bare bracket citations.
</citation-examples>
</requirements>

Each link must reference exactly ONE result id. When multiple results support a claim, cite ALL of them separately.
</instructions>

<style-guide>
{styleGuide}
</style-guide>

<data>
{results}
</data>`;

DEFAULT_PROMPTS.impactAreaGender = `<instructions>
You are a senior writer for the CGIAR annual Portfolio Narrative report. You are given below results data tagged as contributing to Gender Equality (at Principal or Significant level) from across the CGIAR Portfolio; and you are to write a Portfolio-level synthesis of progress on this Impact Area for 2025.

<requirements>
- Summarize Portfolio-level progress on Gender Equality. Emphasize Portfolio-level insights, complementarities across Programs and countries, and any cross-Impact Area synergies, rather than detailed Program-level reporting.
- Where possible, structure the narrative around progress in the Systems Transformation, Resilient Agrifood Systems, and Genetic Innovation areas outlined in the <ia-description-gender> section further below.
- Where relevant, highlight how Portfolio activities align with relevant global commitments (e.g., SDGs).
- Highlight boxes may be used to illustrate cross-cutting contributions where appropriate.
- The narrative should have a max length of 500 words.
- The narrative should be written in a professional yet accessible tone, no jargon or acronyms.
- Follow the writing conventions in the <style-guide> tag.
- For EVERY factual statement derived from the data, embed evidence links using ONLY this exact markdown syntax: [descriptive phrase](Rxx) where xx is the result id from the data.
- YOU CANNOT have paragraphs without citations or references.

<citation-examples>
CORRECT: "[Gender-responsive breeding approaches reached 30,000 women farmers](R17) in East Africa."
WRONG: "...gender approaches [23020]." — NEVER use bare bracket citations.
</citation-examples>
</requirements>

Each link must reference exactly ONE result id. When multiple results support a claim, cite ALL of them separately.
</instructions>

<style-guide>
{styleGuide}
</style-guide>

<ia-description-gender>
{iaDesc_gender}
</ia-description-gender>

<data>
{results}
</data>`;

DEFAULT_PROMPTS.impactAreaClimate = `<instructions>
You are a senior writer for the CGIAR annual Portfolio Narrative report. You are given below results data tagged as contributing to Climate Adaptation and Mitigation (at Principal or Significant level) from across the CGIAR Portfolio; and you are to write a Portfolio-level synthesis of progress on this Impact Area for 2025.

<requirements>
- Summarize Portfolio-level progress on Climate Adaptation and Mitigation. Emphasize Portfolio-level insights, complementarities across Programs and countries, and any cross-Impact Area synergies, rather than detailed Program-level reporting.
- Where possible, structure the narrative around progress in the Systems Transformation, Resilient Agrifood Systems, and Genetic Innovation areas outlined in the <ia-description-climate> section further below.
- Where relevant, highlight how Portfolio activities align with relevant global commitments, including the Paris Agreement.
- Highlight boxes may be used to illustrate cross-cutting contributions where appropriate.
- The narrative should have a max length of 500 words.
- The narrative should be written in a professional yet accessible tone, no jargon or acronyms.
- Follow the writing conventions in the <style-guide> tag.
- For EVERY factual statement derived from the data, embed evidence links using ONLY this exact markdown syntax: [descriptive phrase](Rxx) where xx is the result id from the data.
- YOU CANNOT have paragraphs without citations or references.

<citation-examples>
CORRECT: "[Climate-resilient crop varieties were adopted by 45,000 farmers](R17) across South Asia."
WRONG: "...climate adaptation [23020]." — NEVER use bare bracket citations.
</citation-examples>
</requirements>

Each link must reference exactly ONE result id. When multiple results support a claim, cite ALL of them separately.
</instructions>

<style-guide>
{styleGuide}
</style-guide>

<ia-description-climate>
{iaDesc_climate}
</ia-description-climate>

<data>
{results}
</data>`;

DEFAULT_PROMPTS.impactAreaNutrition = `<instructions>
You are a senior writer for the CGIAR annual Portfolio Narrative report. You are given below results data tagged as contributing to Nutrition, Health and Food Security (at Principal or Significant level) from across the CGIAR Portfolio; and you are to write a Portfolio-level synthesis of progress on this Impact Area for 2025.

<requirements>
- Structure the narrative under these five sub-section headings (use them as markdown ## headings):
  1. **About the Impact Area** — Brief framing of the Nutrition, Health and Food Security Impact Area within the CGIAR Portfolio, drawing on the context in the <ia-description-nutrition> section further below (Systems Transformation, Resilient Agrifood Systems, and Genetic Innovation areas).
  2. **Progress on Nutrition** — Synthesize results contributing specifically to nutrition outcomes. Note which Programs or Accelerators (the "who" from the initiative field), geographies (countries and regions), and result types (Knowledge Products by KP type, Innovations in development and in use, and other outputs) are most prominent.
  3. **Progress on Health** — Synthesize results contributing specifically to health outcomes, following the same pattern: who (Programs/Accelerators), where (geographies), what (output types), and impact (policy changes).
  4. **Progress on Food Security** — Synthesize results contributing specifically to food security outcomes, following the same pattern.
  5. **Intersection with Other Impact Areas** — Highlight where Nutrition, Health and Food Security results also carry Principal or Significant tags for Gender Equality, Climate, Biodiversity, or Poverty Reduction. Use the other Impact Area tag columns in the data to identify and discuss these cross-cutting synergies.
- For each sub-section, surface the types of results that matter most: which Programs or Accelerators are contributing (the "who"), the geographies (the "where"), the type of outputs — Knowledge Products, Innovations in development, Innovations in use (the "what"), and policy changes (the "impact").
- Emphasize Portfolio-level patterns and complementarities across Programs and countries rather than detailed Program-level reporting.
- Where relevant, highlight nutrition-sensitive approaches and how Portfolio activities align with relevant global commitments (e.g., SDGs, the UN Food Systems Summit agenda).
- The narrative should have a max length of 500 words.
- The narrative should be written in a professional yet accessible tone, no jargon or acronyms.
- Follow the writing conventions in the <style-guide> tag.
- For EVERY factual statement derived from the data, embed evidence links using ONLY this exact markdown syntax: [descriptive phrase](Rxx) where xx is the result id from the data.
- YOU CANNOT have paragraphs without citations or references.

<citation-examples>
CORRECT: "[Biofortified crops reached 120,000 households](R17) in sub-Saharan Africa."
WRONG: "...nutrition outcomes [23020]." — NEVER use bare bracket citations.
</citation-examples>
</requirements>

Each link must reference exactly ONE result id. When multiple results support a claim, cite ALL of them separately.
</instructions>

<style-guide>
{styleGuide}
</style-guide>

<ia-description-nutrition>
{iaDesc_nutrition}
</ia-description-nutrition>

<data>
{results}
</data>`;

DEFAULT_PROMPTS.impactAreaBiodiversity = `<instructions>
You are a senior writer for the CGIAR annual Portfolio Narrative report. You are given below results data tagged as contributing to Environmental Health and Biodiversity (at Principal or Significant level) from across the CGIAR Portfolio; and you are to write a Portfolio-level synthesis of progress on this Impact Area for 2025.

<requirements>
- Summarize Portfolio-level progress on Environmental Health and Biodiversity. Emphasize Portfolio-level insights, complementarities across Programs and countries, and any cross-Impact Area synergies, rather than detailed Program-level reporting.
- Where possible, structure the narrative around progress in the Systems Transformation, Resilient Agrifood Systems, and Genetic Innovation areas outlined in the <ia-description-biodiversity> section further below.
- Where relevant, highlight how Portfolio activities align with relevant global commitments, including the Kunming-Montreal Global Biodiversity Framework.
- Highlight boxes may be used to illustrate cross-cutting contributions where appropriate.
- The narrative should have a max length of 500 words.
- The narrative should be written in a professional yet accessible tone, no jargon or acronyms.
- Follow the writing conventions in the <style-guide> tag.
- For EVERY factual statement derived from the data, embed evidence links using ONLY this exact markdown syntax: [descriptive phrase](Rxx) where xx is the result id from the data.
- YOU CANNOT have paragraphs without citations or references.

<citation-examples>
CORRECT: "[Agroforestry systems were established across 80,000 hectares](R17) in the Congo Basin."
WRONG: "...biodiversity outcomes [23020]." — NEVER use bare bracket citations.
</citation-examples>
</requirements>

Each link must reference exactly ONE result id. When multiple results support a claim, cite ALL of them separately.
</instructions>

<style-guide>
{styleGuide}
</style-guide>

<ia-description-biodiversity>
{iaDesc_biodiversity}
</ia-description-biodiversity>

<data>
{results}
</data>`;

DEFAULT_PROMPTS.impactAreaPoverty = `<instructions>
You are a senior writer for the CGIAR annual Portfolio Narrative report. You are given below results data tagged as contributing to Poverty Reduction, Livelihoods and Jobs (at Principal or Significant level) from across the CGIAR Portfolio; and you are to write a Portfolio-level synthesis of progress on this Impact Area for 2025.

<requirements>
- Summarize Portfolio-level progress on Poverty Reduction, Livelihoods and Jobs. Emphasize Portfolio-level insights, complementarities across Programs and countries, and any cross-Impact Area synergies, rather than detailed Program-level reporting.
- Where possible, structure the narrative around progress in the Systems Transformation, Resilient Agrifood Systems, and Genetic Innovation areas outlined in the <ia-description-poverty> section further below.
- Where relevant, highlight inclusive growth approaches and how Portfolio activities align with relevant global commitments (e.g., SDG 1, SDG 8).
- Highlight boxes may be used to illustrate cross-cutting contributions where appropriate.
- The narrative should have a max length of 500 words.
- The narrative should be written in a professional yet accessible tone, no jargon or acronyms.
- Follow the writing conventions in the <style-guide> tag.
- For EVERY factual statement derived from the data, embed evidence links using ONLY this exact markdown syntax: [descriptive phrase](Rxx) where xx is the result id from the data.
- YOU CANNOT have paragraphs without citations or references.

<citation-examples>
CORRECT: "[Market access interventions increased incomes for 25,000 smallholder households](R17) in West Africa."
WRONG: "...poverty reduction [23020]." — NEVER use bare bracket citations.
</citation-examples>
</requirements>

Each link must reference exactly ONE result id. When multiple results support a claim, cite ALL of them separately.
</instructions>

<style-guide>
{styleGuide}
</style-guide>

<ia-description-poverty>
{iaDesc_poverty}
</ia-description-poverty>

<data>
{results}
</data>`;

DEFAULT_PROMPTS.regionalAfrica = `<instructions>
You are a senior writer for the CGIAR annual Portfolio Narrative report. You are given below results data from CGIAR Portfolio activities implemented in Africa; and you are to write a Portfolio-level regional synthesis for Africa for 2025.

<requirements>
- Summarize what the Portfolio is delivering in Africa, covering key thematic areas, the scale of investment, main results achieved, and the role of partnerships.
- Where the data supports it, note what is new or different compared with the 2022-2024 CGIAR Portfolio, highlighting changes in focus, partnerships, or delivery approaches in the region.
- Emphasize Portfolio-level patterns and complementarities across Programs and countries rather than detailed Program-level reporting.
- The narrative should have a max length of 500 words.
- The narrative should be written in a professional yet accessible tone, no jargon or acronyms.
- Follow the writing conventions in the <style-guide> tag.
- For EVERY factual statement derived from the data, embed evidence links using ONLY this exact markdown syntax: [descriptive phrase](Rxx) where xx is the result id from the data.
- YOU CANNOT have paragraphs without citations or references.

<citation-examples>
CORRECT: "[Drought-tolerant maize reached 200,000 farmers](R17) across Eastern and Southern Africa."
WRONG: "...results in Africa [23020]." — NEVER use bare bracket citations.
</citation-examples>
</requirements>

Each link must reference exactly ONE result id. When multiple results support a claim, cite ALL of them separately.
</instructions>

<style-guide>
{styleGuide}
</style-guide>

<data>
{results}
</data>`;

DEFAULT_PROMPTS.regionalAmericas = `<instructions>
You are a senior writer for the CGIAR annual Portfolio Narrative report. You are given below results data from CGIAR Portfolio activities implemented in The Americas; and you are to write a Portfolio-level regional synthesis for The Americas for 2025.

<requirements>
- Summarize what the Portfolio is delivering in The Americas, covering key thematic areas, the scale of investment, main results achieved, and the role of partnerships.
- Where the data supports it, note what is new or different compared with the 2022-2024 CGIAR Portfolio, highlighting changes in focus, partnerships, or delivery approaches in the region.
- Emphasize Portfolio-level patterns and complementarities across Programs and countries rather than detailed Program-level reporting.
- The narrative should have a max length of 500 words.
- The narrative should be written in a professional yet accessible tone, no jargon or acronyms.
- Follow the writing conventions in the <style-guide> tag.
- For EVERY factual statement derived from the data, embed evidence links using ONLY this exact markdown syntax: [descriptive phrase](Rxx) where xx is the result id from the data.
- YOU CANNOT have paragraphs without citations or references.

<citation-examples>
CORRECT: "[Integrated pest management tools were adopted by 18,000 farmers](R17) across Latin America."
WRONG: "...results in The Americas [23020]." — NEVER use bare bracket citations.
</citation-examples>
</requirements>

Each link must reference exactly ONE result id. When multiple results support a claim, cite ALL of them separately.
</instructions>

<style-guide>
{styleGuide}
</style-guide>

<data>
{results}
</data>`;

DEFAULT_PROMPTS.regionalAsia = `<instructions>
You are a senior writer for the CGIAR annual Portfolio Narrative report. You are given below results data from CGIAR Portfolio activities implemented in Asia; and you are to write a Portfolio-level regional synthesis for Asia for 2025.

<requirements>
- Summarize what the Portfolio is delivering in Asia, covering key thematic areas, the scale of investment, main results achieved, and the role of partnerships.
- Where the data supports it, note what is new or different compared with the 2022-2024 CGIAR Portfolio, highlighting changes in focus, partnerships, or delivery approaches in the region.
- Emphasize Portfolio-level patterns and complementarities across Programs and countries rather than detailed Program-level reporting.
- The narrative should have a max length of 500 words.
- The narrative should be written in a professional yet accessible tone, no jargon or acronyms.
- Follow the writing conventions in the <style-guide> tag.
- For EVERY factual statement derived from the data, embed evidence links using ONLY this exact markdown syntax: [descriptive phrase](Rxx) where xx is the result id from the data.
- YOU CANNOT have paragraphs without citations or references.

<citation-examples>
CORRECT: "[Flood-tolerant rice varieties were released in five countries](R17) across South and Southeast Asia."
WRONG: "...results in Asia [23020]." — NEVER use bare bracket citations.
</citation-examples>
</requirements>

Each link must reference exactly ONE result id. When multiple results support a claim, cite ALL of them separately.
</instructions>

<style-guide>
{styleGuide}
</style-guide>

<data>
{results}
</data>`;

DEFAULT_PROMPTS.portfolioPartnerships = `<instructions>
You are a senior writer for the CGIAR annual Portfolio Narrative report. You are given below results data from across the entire CGIAR Portfolio related to partnerships; and you are to write a Portfolio-level synthesis of the partnership landscape for 2025.

<requirements>
- Summarize the Portfolio-level partnership landscape, structured around CGIAR's Engagement Framework for Partnerships and Advocacy.
- Cover main partner types and their strategic roles across the Portfolio (e.g., national agricultural research and extension systems, advanced research institutions, development finance institutions, private sector, civil society).
- Describe how partnerships contributed to results delivery during 2025, emphasizing co-ownership, complementarity, and shared accountability.
- Highlight evidence of alignment and complementarity between partner contributions and Portfolio priorities.
- Note the key added value that partnerships brought to the Portfolio, as well as any risks or challenges in the partnership landscape.
- This is a Portfolio-level synthesis — DO NOT describe individual Program partnerships in detail; instead highlight Portfolio-wide patterns and strategic themes.
- The narrative should have a max length of 500 words.
- The narrative should be written in a professional yet accessible tone, no jargon or acronyms.
- Follow the writing conventions in the <style-guide> tag.
- For EVERY factual statement derived from the data, embed evidence links using ONLY this exact markdown syntax: [descriptive phrase](Rxx) where xx is the result id from the data.
- YOU CANNOT have paragraphs without citations or references.

<citation-examples>
CORRECT: "[Joint research with national partners contributed to policy adoption in seven countries](R17)."
WRONG: "...partnership outcomes [23020]." — NEVER use bare bracket citations.
</citation-examples>
</requirements>

Each link must reference exactly ONE result id. When multiple results support a claim, cite ALL of them separately.
</instructions>

<style-guide>
{styleGuide}
</style-guide>

<data>
{results}
</data>`;

// Simulate LLM response for demo purposes when API key is not available
function generateMockResponse(prompt, section) {
  const mockResponses = {
    executiveSummary: `In 2025, this program made significant strides in advancing agricultural research and development across multiple regions. The year was marked by notable achievements in knowledge product development, capacity building, and innovation deployment.

Key highlights include the successful development and dissemination of improved agricultural practices reaching over 50,000 farmers across target countries. Our research outputs contributed to policy changes in three countries, enhancing the enabling environment for sustainable agriculture.

The program strengthened partnerships with national agricultural research systems, leading to enhanced co-design and implementation of research activities. Cross-portfolio collaboration resulted in synergistic outcomes, particularly in climate adaptation and gender-inclusive approaches.

Challenges related to funding constraints and operational disruptions were addressed through adaptive management, including resource reallocation and revised implementation timelines. These adjustments ensured continued progress toward our 2030 outcomes while maintaining research quality and stakeholder engagement.

Looking forward, the program is well-positioned to build on these achievements and deepen impact in target regions.`,

    progressNarrative: `Progress along the theory of change demonstrated strong alignment with planned outcomes. Knowledge products developed during the year directly contributed to capacity building objectives, with evidence of uptake by key stakeholders.

Innovation development activities resulted in three new technologies reaching the validation stage, with promising adoption indicators in pilot regions. The integration of gender-responsive approaches enhanced the inclusivity of research outputs.

Country-focused results were particularly notable in East Africa, where collaborative efforts with national partners accelerated technology transfer. Climate adaptation research advanced our understanding of resilience pathways for smallholder farmers.

Portfolio linkages strengthened delivery, with joint activities generating outcomes that individual programs could not achieve alone. This collaborative approach exemplifies the power of integrated research systems.`,

    areaOfWorkProgress: `This Area of Work achieved substantial progress against its planned indicators, reaching approximately 85% of targets for the reporting period.

Key outputs include comprehensive technical documentation, training materials adopted by partner institutions, and validated innovations ready for scaling. The collaborative nature of the work, involving multiple CGIAR centers and national partners, enhanced both the quality and relevance of outputs.

W3/bilateral projects contributed additional resources and expertise, enabling expanded geographic coverage and deeper engagement with stakeholders. These projects aligned well with pooled activities, creating synergies that amplified overall impact.

Challenges in specific implementation contexts were addressed through adaptive management, with lessons learned informing future planning and resource allocation decisions.`,

    partnershipOverview: `Partnerships were central to achieving 2025 results, with strategic engagement of national research systems, development organizations, and private sector actors.

Key partners contributed to research design, implementation, and scaling activities. NARES partners played essential roles in contextualizing interventions and building local capacity for sustained impact.

The partnership landscape reflects CGIAR's commitment to co-ownership and shared accountability for outcomes.`,

    portfolioLinkagesOverview: `This Accelerator maintained active portfolio linkages with multiple Science Programs during 2025, contributing cross-cutting expertise and shared methodologies that strengthened program delivery.

Joint results with linked Programs demonstrate the value of integrated approaches across the CGIAR portfolio, with collaborative outputs advancing shared 2030 outcome targets. Cross-program collaboration enhanced research quality and expanded geographic reach.`,

    portfolioOverview: `The 2025 reporting year marked the first full year of operation under the new 2025-2030 CGIAR Portfolio, with significant effort directed toward establishing effective management arrangements, operationalizing performance and results management systems, and building coherence across the ten Science Programs and five Accelerators.

Early in the year, inception activities focused on clarifying governance structures, aligning W3/Bilateral funding to portfolio priorities, and establishing shared results frameworks. These foundational investments strengthened the basis for integrated delivery and set the stage for more cohesive reporting across the Portfolio.

Looking ahead to 2026, the Portfolio is well positioned to deepen integration across Programs, accelerate results at scale, and sharpen its contribution to the five CGIAR Impact Areas. Continued investment in shared infrastructure and cross-Portfolio learning will be critical to realizing the full potential of the new architecture.`,

    impactAreaGender: `Across the CGIAR Portfolio, 2025 saw sustained investment in gender-responsive research and development, with results cutting across crop improvement, natural resource management, and food systems innovation. Portfolio-wide patterns indicate growing integration of gender considerations into research design rather than as a standalone add-on.

Complementarities across Programs were evident in shared approaches to women's empowerment in value chains and gender-disaggregated data collection. These cross-Portfolio synergies strengthened the evidence base for gender-transformative interventions and contributed to progress toward relevant Sustainable Development Goals.

The Portfolio's gender results in 2025 reflect a maturing approach that connects gender equality to broader outcomes on nutrition, poverty reduction, and climate resilience, underscoring the importance of maintaining gender as a cross-cutting priority across all Impact Areas.`,

    impactAreaClimate: `CGIAR's 2025 Portfolio results on Climate Adaptation and Mitigation reflect the breadth and depth of investment in climate-smart agriculture, agroforestry, and resilience-building across the world's most vulnerable food systems. Portfolio-wide, results demonstrate a strong pipeline from research to scaled adoption of climate-resilient technologies.

Alignment with the Paris Agreement goals was evident in the Portfolio's contributions to nationally determined contributions in several partner countries, where CGIAR research informed climate policy and practice. Cross-Impact Area synergies with biodiversity and nutrition outcomes were also notable, reflecting the interconnected nature of climate and food security challenges.

As climate risks intensify, the Portfolio's 2025 results provide a strong foundation for accelerating adaptation and mitigation outcomes in 2026, particularly through deeper engagement with national climate policy processes and expanded investment in climate risk analytics.`,

    impactAreaNutrition: `Progress on Nutrition, Health and Food Security across the CGIAR Portfolio in 2025 reflected both the scale and quality of investment in biofortification, dietary diversity, and food systems transformation. Nutrition-sensitive approaches were embedded throughout the Portfolio, connecting crop improvement to consumer health outcomes.

Portfolio-level patterns highlighted the importance of integrating nutrition outcomes into the design of agricultural interventions, with several Programs demonstrating how gender-responsive and nutrition-sensitive approaches can reinforce each other. These complementarities contributed to progress on relevant global commitments, including the Sustainable Development Goals on zero hunger and good health.

Sustaining this momentum in 2026 will require continued investment in evidence generation on nutrition pathways, as well as stronger engagement with national nutrition policies and food environment actors to translate research results into lasting dietary improvements.`,

    impactAreaBiodiversity: `The CGIAR Portfolio's contributions to Environmental Health and Biodiversity in 2025 spanned genetic resource conservation, ecosystem restoration, and sustainable land and water management. Portfolio-wide, results reflect a diverse set of interventions aimed at maintaining the natural capital on which food systems depend.

Alignment with the Kunming-Montreal Global Biodiversity Framework was evident in several Portfolio workstreams, particularly those related to the conservation and sustainable use of plant genetic resources and the restoration of degraded agricultural landscapes. Cross-Impact Area synergies with climate adaptation were a recurring theme, with biodiversity outcomes often achieved alongside progress on resilience.

The Portfolio's 2025 biodiversity results demonstrate the critical role of agricultural research in supporting global biodiversity goals, and underscore the need to mainstream biodiversity considerations across all Portfolio activities in the years ahead.`,

    impactAreaPoverty: `The CGIAR Portfolio's 2025 results on Poverty Reduction, Livelihoods and Jobs reflect a broad set of interventions aimed at improving the economic wellbeing of smallholder farmers, rural women, and other vulnerable groups across target countries. Inclusive growth approaches were central to the Portfolio's design, with results contributing to progress on SDG 1 and SDG 8.

Portfolio-level patterns highlighted the importance of market systems development, value chain upgrading, and enterprise development in translating agricultural research results into income and livelihood gains. Cross-Impact Area synergies with gender equality and nutrition outcomes reinforced the case for integrated approaches to poverty reduction.

Building on 2025 results, the Portfolio is well placed to deepen its contribution to inclusive economic growth in 2026, particularly through greater engagement with the private sector and development finance institutions to catalyze investment in smallholder-inclusive value chains.`,

    regionalAfrica: `Africa remained the largest recipient of CGIAR Portfolio investment in 2025, with results spanning crop improvement, natural resource management, food systems innovation, and policy engagement across sub-Saharan and North Africa. Portfolio delivery in the region reflected both the breadth of thematic focus and the depth of engagement with national partners.

Compared with the 2022-2024 Portfolio, the new architecture has enabled more deliberate integration of workstreams across Programs in the region, with joint results increasingly reflecting shared priorities rather than parallel activities. Partnerships with national agricultural research and extension systems, regional bodies, and development partners continued to be the primary mechanism for achieving scale and sustainability.

Main results in 2025 included significant advances in technology adoption, policy influence, and capacity strengthening, with evidence of outcomes reaching smallholder farmers across Eastern, Western, and Southern Africa. These results position the Portfolio well for accelerated delivery in 2026 as Programs move beyond their inception phase.`,

    regionalAmericas: `CGIAR's Portfolio presence in The Americas in 2025 was characterized by targeted investment in climate adaptation, food systems transformation, and biodiversity conservation, with a strong emphasis on Latin America and the Caribbean. The Portfolio's regional footprint reflected both longstanding center presences and new programmatic priorities under the 2025-2030 architecture.

Compared with the previous Portfolio cycle, 2025 saw increased attention to the nexus of climate, food security, and livelihoods in the region, with several Programs deepening their engagement with national governments and development partners on policy-relevant research. Partnerships with advanced research institutions and regional bodies played an important role in amplifying the reach and relevance of CGIAR results.

Key results in 2025 included advances in sustainable land management, crop diversification, and market systems development, contributing to improved livelihoods and resilience for smallholder communities across the region. These outcomes provide a strong base for expanding Portfolio impact in The Americas in 2026.`,

    regionalAsia: `Asia remained a critical region for CGIAR Portfolio delivery in 2025, with results spanning rice and wheat systems, water and land management, nutrition, and policy engagement across South, Southeast, and Central Asia. The Portfolio's regional investment reflected both the importance of Asian food systems for global food security and the depth of CGIAR's longstanding partnerships in the region.

The transition to the 2025-2030 Portfolio brought new opportunities for integration across Programs in Asia, with joint workstreams emerging around climate adaptation, digital agriculture, and food systems transformation. Compared with the 2022-2024 cycle, the new architecture has enabled more strategic alignment of Portfolio activities with national development priorities and regional food security agendas.

Main results in 2025 included significant advances in the development and adoption of improved crop varieties, sustainable intensification practices, and evidence-based policy, with outcomes reaching farmers across South and Southeast Asia in particular. These results underscore Asia's continued centrality to the CGIAR Portfolio's contribution to global food security.`,

    portfolioPartnerships: `The CGIAR Portfolio's 2025 partnership landscape reflected the diversity and strategic ambition of the new 2025-2030 architecture, with partnerships spanning national agricultural research and extension systems, advanced research institutions, development finance institutions, the private sector, and civil society. Structured around CGIAR's Engagement Framework for Partnerships and Advocacy, the Portfolio's approach emphasized co-ownership, complementarity, and shared accountability for results.

National agricultural research and extension systems remained the most significant partner category in terms of volume and reach, with co-design and co-implementation of research activities contributing directly to the relevance and scalability of Portfolio results. Development finance institutions and bilateral donors played an important role in mobilizing W3/Bilateral resources aligned to Portfolio priorities, while advanced research institutions contributed cutting-edge expertise and global networks.

Key added value from partnerships in 2025 included expanded geographic reach, enhanced stakeholder legitimacy, and accelerated pathways to adoption and policy influence. Risks in the partnership landscape included dependency on a limited number of large bilateral donors and uneven partner capacity in some regions. Addressing these risks through more diversified and equitable partnership arrangements will be a priority for the Portfolio in 2026.`,
  };

  // Handle dynamic aowNarrative_* keys → use areaOfWorkProgress mock
  if (section.startsWith('aowNarrative_')) {
    return mockResponses.areaOfWorkProgress;
  }

  // Handle dynamic impactArea_* and regional_* keys → use their own mock or fall through
  if (section.startsWith('impactArea') || section.startsWith('regional')) {
    return mockResponses[section] || 'Generated content will appear here based on the filtered data.';
  }

  return mockResponses[section] || 'Generated content will appear here based on the filtered data.';
}

// Call Anthropic Claude API
async function callAnthropic(formattedPrompt, apiKey, model) {
  const response = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01',
    },
    body: JSON.stringify({
      model: model || 'claude-sonnet-4-20250514',
      max_tokens: 5000,
      messages: [
        {
          role: 'user',
          content: formattedPrompt,
        },
      ],
    }),
  });

  if (!response.ok) {
    throw new Error(`Anthropic API request failed: ${response.statusText}`);
  }

  const result = await response.json();
  return result.content[0].text;
}

// Call OpenAI API
async function callOpenAI(formattedPrompt, apiKey, model) {
  const response = await fetch('https://api.openai.com/v1/chat/completions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`,
    },
    body: JSON.stringify({
      model: model || 'gpt-4.1-mini',
      max_tokens: 5000,
      messages: [
        {
          role: 'system',
          content: 'You are a professional writer for CGIAR, an international agricultural research organization.',
        },
        {
          role: 'user',
          content: formattedPrompt,
        },
      ],
    }),
  });

  if (!response.ok) {
    throw new Error(`OpenAI API request failed: ${response.statusText}`);
  }

  const result = await response.json();
  return result.choices[0].message.content;
}

// Resolve dynamic section keys to their prompt key in DEFAULT_PROMPTS
export function resolvePromptKey(section) {
  if (section.startsWith('aowNarrative_')) return 'areaOfWorkProgress';
  if (section.startsWith('impactArea')) return section; // impactAreaGender, etc.
  if (section.startsWith('regional')) return section; // regionalAfrica, etc.
  return section;
}

// Main function to generate narrative content
// Fallback chain: if the user-selected model fails, try each in order.
const FALLBACK_CHAIN = ['claude-sonnet-4-6', 'gpt-4.1'];

// Token-limit errors should not trigger a fallback — the prompt itself is too large.
const TOKEN_LIMIT_RE = /max_tokens|context_length|token.limit|too.many.tokens|content_too_large|maximum.context|token_count|exceeds.*limit/i;

function isTokenLimitError(message) {
  return TOKEN_LIMIT_RE.test(message || '');
}

export async function generateNarrative(section, data, customPrompt = null, settings = {}, customSynthesisPrompt = null) {
  const resolvedSection = resolvePromptKey(section);
  const prompt = customPrompt || DEFAULT_PROMPTS[resolvedSection];
  const { model, onProgress, abortSignal } = settings;

  // Format the prompt with data
  const formattedPrompt = formatPrompt(prompt, data);

  // ── Check whether the prompt exceeds the model's context window ──────────
  const tokenEstimate = estimateTokens(formattedPrompt);
  const contextLimit = MODEL_CONTEXT_LIMITS[model] || 200_000;
  const safeLimit = Math.floor(contextLimit * CHUNKING_SAFETY_FACTOR);

  if (tokenEstimate > safeLimit && data.results && data.results.length > 1) {
    console.log(
      `[LLM] Prompt ~${tokenEstimate.toLocaleString()} tokens exceeds safe limit ` +
      `~${safeLimit.toLocaleString()} for ${model}. Switching to chunked generation.`
    );
    return generateNarrativeChunked(section, data, prompt, settings, tokenEstimate, safeLimit, customSynthesisPrompt);
  }

  // ── Single-call path (prompt fits within context window) ─────────────────
  const { callBatchService } = await import('./batchService.js');

  // Build the list of models to try: user-selected first, then fallbacks (skipping duplicates)
  const modelsToTry = [model, ...FALLBACK_CHAIN.filter(m => m !== model)];
  const errors = [];

  for (const currentModel of modelsToTry) {
    try {
      console.log(`[LLM] Calling ${currentModel} for ${section}…`);
      if (errors.length > 0) {
        if (onProgress) onProgress({ phase: 'retrying', progress: null, jobId: null });
      }

      const content = await callBatchService(formattedPrompt, {
        model: currentModel,
        promptName: section,
        onProgress,
        abortSignal,
      });

      const fallback = currentModel !== model ? ` (fallback from ${model})` : '';
      console.log(`[LLM] ${currentModel} succeeded for ${section}${fallback}`);

      return {
        success: true,
        content,
        isMock: false,
        ...(currentModel !== model && { fallbackUsed: currentModel }),
      };
    } catch (error) {
      if (error.name === 'AbortError') {
        return { success: false, error: 'Generation cancelled', content: '', isMock: false };
      }

      // Token-limit errors are not retryable — the prompt itself is too large
      if (isTokenLimitError(error.message)) {
        console.error('LLM token limit error (no fallback):', error);
        return { success: false, error: error.message, content: '', isMock: false };
      }

      console.warn(`Model ${currentModel} failed: ${error.message}`);
      errors.push({ model: currentModel, message: error.message });
    }
  }

  // All models in the chain failed
  const errorSummary = errors.map(e => `${e.model}: ${e.message}`).join('; ');
  console.error('All models failed:', errorSummary);
  return {
    success: false,
    error: `All models failed — ${errorSummary}`,
    content: '',
    isMock: false,
  };
}

// ── Chunked generation ─────────────────────────────────────────────────────────
// When the dataset is too large for a single prompt, split data.results into N
// chunks, run each through the same prompt template in parallel, then synthesize
// the partial outputs into a single coherent narrative with one final LLM call.

async function generateNarrativeChunked(section, data, promptTemplate, settings, tokenEstimate, safeLimit, customSynthesisPrompt = null) {
  const { model, onProgress, abortSignal } = settings;
  const { callBatchService } = await import('./batchService.js');

  // ── Determine how many chunks we need ──────────────────────────────────────
  // Non-data portion = everything in the prompt except the JSON-stringified results
  const dataJson = JSON.stringify(data.results);
  const dataTokens = estimateTokens(dataJson);
  const nonDataTokens = tokenEstimate - dataTokens;

  // Available budget for data in each chunk — respect BOTH the LLM context window
  // AND the batch service's Excel transport limit (MAX_CHUNK_CHARS).
  const contextBudget = Math.max(safeLimit - nonDataTokens, 50_000); // floor at 50k
  const transportBudget = Math.floor(MAX_CHUNK_CHARS / 4); // chars → tokens
  const availableDataTokens = Math.min(contextBudget, transportBudget);
  const numChunks = Math.ceil(dataTokens / availableDataTokens);
  const resultsPerChunk = Math.ceil(data.results.length / numChunks);

  // Split results into roughly equal chunks
  const chunks = [];
  for (let i = 0; i < data.results.length; i += resultsPerChunk) {
    chunks.push(data.results.slice(i, i + resultsPerChunk));
  }

  console.log(
    `[LLM] Chunked generation: ${chunks.length} chunks ` +
    `(${data.results.length} results, ~${resultsPerChunk} per chunk)`
  );

  if (onProgress) {
    onProgress({
      phase: 'chunking',
      progress: 0,
      totalChunks: chunks.length,
      completedChunks: 0,
    });
  }

  // ── Phase 1: Process all chunks in parallel ────────────────────────────────
  const chunkProgresses = new Array(chunks.length).fill(0);
  const modelsToTry = [model, ...FALLBACK_CHAIN.filter(m => m !== model)];

  const chunkPromises = chunks.map((chunkResults, idx) => {
    const chunkData = { ...data, results: chunkResults };
    const chunkPrompt = formatPrompt(promptTemplate, chunkData);

    return (async () => {
      for (const currentModel of modelsToTry) {
        try {
          if (abortSignal?.aborted) throw new DOMException('Aborted', 'AbortError');

          const content = await callBatchService(chunkPrompt, {
            model: currentModel,
            promptName: `${section}_chunk${idx + 1}of${chunks.length}`,
            onProgress: (p) => {
              // Track per-chunk progress; report weighted average across all chunks
              if (p.progress != null) chunkProgresses[idx] = p.progress;
              const completedChunks = chunkProgresses.filter(v => v >= 100).length;
              const avgProgress = chunkProgresses.reduce((a, b) => a + b, 0) / chunks.length;
              if (onProgress) {
                onProgress({
                  phase: 'chunking',
                  // Chunks occupy 0-80% of overall progress; synthesis is 80-100%
                  progress: avgProgress * 0.80,
                  totalChunks: chunks.length,
                  completedChunks,
                });
              }
            },
            abortSignal,
          });

          chunkProgresses[idx] = 100;
          console.log(`[LLM] Chunk ${idx + 1}/${chunks.length} completed on ${currentModel}`);
          return content;
        } catch (error) {
          if (error.name === 'AbortError') throw error;
          if (isTokenLimitError(error.message)) throw error;
          console.warn(`[LLM] Chunk ${idx + 1}: ${currentModel} failed — ${error.message}`);
          continue; // try next model in fallback chain
        }
      }
      throw new Error(`Chunk ${idx + 1}/${chunks.length} failed on all models`);
    })();
  });

  let chunkOutputs;
  try {
    chunkOutputs = await Promise.all(chunkPromises);
  } catch (error) {
    if (error.name === 'AbortError') {
      return { success: false, error: 'Generation cancelled', content: '', isMock: false };
    }
    console.error('[LLM] Chunked generation failed:', error.message);
    return { success: false, error: error.message, content: '', isMock: false };
  }

  // ── Phase 2: Synthesis — merge partial narratives into one ─────────────────
  if (onProgress) {
    onProgress({ phase: 'synthesizing', progress: 85, totalChunks: chunks.length, completedChunks: chunks.length });
  }

  const synthesisPrompt = buildSynthesisPrompt(section, data, chunkOutputs, customSynthesisPrompt);
  console.log(`[LLM] Synthesizing ${chunkOutputs.length} partial narratives…`);

  for (const currentModel of modelsToTry) {
    try {
      if (abortSignal?.aborted) throw new DOMException('Aborted', 'AbortError');

      const synthesized = await callBatchService(synthesisPrompt, {
        model: currentModel,
        promptName: `${section}_synthesis`,
        onProgress: (p) => {
          if (onProgress) {
            onProgress({
              phase: 'synthesizing',
              progress: 85 + ((p.progress || 0) * 0.15),
              totalChunks: chunks.length,
              completedChunks: chunks.length,
            });
          }
        },
        abortSignal,
      });

      console.log(`[LLM] Synthesis completed on ${currentModel} (${chunkOutputs.length} chunks → 1 narrative)`);
      return {
        success: true,
        content: synthesized,
        isMock: false,
        chunked: true,
        chunkCount: chunks.length,
        ...(currentModel !== model && { fallbackUsed: currentModel }),
      };
    } catch (error) {
      if (error.name === 'AbortError') {
        return { success: false, error: 'Generation cancelled', content: '', isMock: false };
      }
      console.warn(`[LLM] Synthesis: ${currentModel} failed — ${error.message}`);
      continue;
    }
  }

  return {
    success: false,
    error: `All ${chunkOutputs.length} data chunks processed but synthesis failed on all models`,
    content: '',
    isMock: false,
  };
}

// ── Default synthesis prompt template ────────────────────────────────────────
// Exported so the UI can display / let users edit it.
// Placeholders: {section}, {entityType}, {entityName}, {chunkCount}, {partials}
export const DEFAULT_SYNTHESIS_PROMPT = `<instructions>
You are synthesizing multiple narrative drafts into a single, polished narrative for the "{section}" section of a CGIAR {entityType} report{entityNameClause}.

Because the underlying dataset was too large for a single LLM call, it was split into {chunkCount} parts. Each part was analyzed independently using the same prompt and produced the partial narrative shown below. Your task is to merge them into ONE coherent, publication-ready narrative.

RULES:
1. Combine all {chunkCount} partial narratives into a single, well-structured narrative.
2. Remove redundant or repeated content — deduplicate facts, themes, and examples.
3. Ensure smooth, logical transitions between topics and paragraphs.
4. Preserve EVERY evidence link in the format [descriptive phrase](Rxx) — do not drop, renumber, or invent citations.
5. Maintain the same professional tone, markdown formatting, and structure used in the partials.
6. The merged narrative MUST be between 400 and 600 words. This is a strict target. Under absolutely no circumstances may the output exceed 1,500 words — this is a hard, impassable limit. Prefer conciseness: ruthlessly cut redundancy to stay near 500 words.
7. Do NOT add information that does not appear in the partial narratives.
8. Do NOT mention that the content was generated in chunks, synthesized, or merged.
</instructions>

{partials}

Write the merged narrative now. Output ONLY the final narrative text in markdown format.`;

// Build the prompt for the synthesis (merge) call
function buildSynthesisPrompt(section, data, chunkOutputs, customTemplate) {
  const entityType = data.reportType === 'accelerator' ? 'Accelerator' : 'Science Program';
  const entityName = data.entityName || '';

  const partials = chunkOutputs
    .map((output, i) => `<partial-narrative part="${i + 1}">\n${output}\n</partial-narrative>`)
    .join('\n\n');

  const template = customTemplate || DEFAULT_SYNTHESIS_PROMPT;

  return template
    .replace(/\{section\}/g, section)
    .replace(/\{entityType\}/g, entityType)
    .replace(/\{entityNameClause\}/g, entityName ? ` for ${entityName}` : '')
    .replace(/\{entityName\}/g, entityName)
    .replace(/\{chunkCount\}/g, String(chunkOutputs.length))
    .replace(/\{partials\}/g, partials);
}

// Format prompt with actual data — results as compact JSON, context as plain text
export function formatPrompt(template, data) {
  let formatted = template;

  // Replace entity type placeholder (Science Program vs Accelerator)
  formatted = formatted.replace('{entityType}', data.reportType === 'accelerator' ? 'Accelerator' : 'Science Program');

  // Replace entity name placeholder (actual program/accelerator name from Factsheet)
  if (data.entityName) {
    formatted = formatted.replaceAll('{entityName}', data.entityName);
  }

  if (data.results) {
    formatted = formatted.replace('{results}', () => JSON.stringify(data.results));
  }

  if (data.context) {
    formatted = formatted.replace('{context}', () => data.context);
  }

  if (data.aow) {
    formatted = formatted.replaceAll('{aow}', data.aow);
    formatted = formatted.replaceAll('{areaOfWorkName}', data.aow);
  }

  if (data.impactAreas) {
    formatted = formatted.replace('{impactAreas}', data.impactAreas.join(', '));
  }

  if (data.targets) {
    const targetsText = data.targets
      .map(t => `- ${t.indicator}: Target ${t.target2025}`)
      .join('\n');
    formatted = formatted.replace('{targets}', () => targetsText);
  }

  if (data.linkages) {
    formatted = formatted.replace('{linkages}', () => JSON.stringify(data.linkages));
  }

  if (data.partners) {
    formatted = formatted.replace('{partners}', () => JSON.stringify(data.partners));
  }

  if (data.partner) {
    formatted = formatted.replace('{partner}', () => data.partner);
  }

  if (data.result) {
    formatted = formatted.replace('{result}', () => JSON.stringify(data.result));
  }

  // Replace {description} placeholder — fill with content or strip the block entirely
  if (data.description) {
    formatted = formatted.replace('{description}', () => data.description);
  } else {
    // No description loaded — remove the entire <description> block
    formatted = formatted.replace(/\n*<description>\n\{description\}\n<\/description>/g, '');
  }

  // Replace Impact Area description placeholders
  const iaKeys = ['gender', 'climate', 'nutrition', 'biodiversity', 'poverty'];
  for (const iaKey of iaKeys) {
    const placeholder = `{iaDesc_${iaKey}}`;
    const tag = `ia-description-${iaKey}`;
    if (data[`iaDesc_${iaKey}`]) {
      formatted = formatted.replace(placeholder, () => data[`iaDesc_${iaKey}`]);
    } else {
      // Remove the entire block if no description available
      const blockRegex = new RegExp(`\\n*<${tag}>\\n\\{iaDesc_${iaKey}\\}\\n<\\/${tag}>`, 'g');
      formatted = formatted.replace(blockRegex, '');
    }
  }

  // Replace {styleGuide} placeholder with full CGIAR style guide content
  formatted = formatted.replace('{styleGuide}', () => CGIAR_STYLE_GUIDE);

  return formatted;
}

// Generate multiple sections at once
export async function generateAllSections(data, customPrompts = {}, settings = {}) {
  const sections = ['executiveSummary', 'progressNarrative', 'areaOfWorkProgress', 'partnershipOverview'];

  const results = {};

  for (const section of sections) {
    results[section] = await generateNarrative(
      section,
      data,
      customPrompts[section],
      settings
    );
  }

  return results;
}
